import React, { memo, useMemo } from 'react';
import { FunctionField, useTranslate, TextField, SelectField } from 'react-admin';
import { Box, Link, Tooltip, Typography } from '@material-ui/core';
import resourceSlug from '../../../../constant/resource-slug';
import DrawerSimpleShowLayout from '../../../../base/components/ra/drawers/DrawerSimpleShowLayout';
import { AmountField, DateField } from '../../../../base/components/ra/fields';
import CustomLinkField from '../../../../base/components/ra/fields/customLink.field';
import { DATETIME_DISPLAY_FORMAT } from '../../../../constant';
import ArrayWithPaginationField from '../../../../base/components/ra/fields/arrayWithPagination.field';
import PaymentStatus from '../../../payment/components/PaymentStatus';
import { PAYMENT_METHOD } from '../../../../constant/invoice';
import { truncate } from '../../../../services/util/stringUtil';
import ProofsPreview from '../../../payment/components/ProofPreview';

const PaymentTable = memo(({ record }) => {
  const t = useTranslate();

  const sortedRecord = useMemo(
    () => ({
      ...record,
      payments: record?.payments?.sort?.((a, b) => b.createdAt - a.createdAt),
    }),
    [record],
  );

  return (
    record?.payments && (
      <Box
        component={DrawerSimpleShowLayout}
        sx={{
          flexGrow: 1,
          bgcolor: 'white',
          borderRadius: 12,
          padding: '24px !important',
        }}
        record={sortedRecord}
      >
        <Box
          component={Typography}
          variant="h4"
          sx={{
            marginBottom: '18px !important',
          }}
        >
          {t('resources.invoice.paymentDetails')}
        </Box>
        <ArrayWithPaginationField
          label={false}
          source="payments"
          rowClick={(id) => `/${resourceSlug.PAYMENT}?paymentId=${id}`}
          style={{
            maxHeight: 'unset',
          }}
        >
          <TextField source="id" label="resources.payment.fields.id" sortable={false} />
          <DateField
            source="createdAt"
            sortable={false}
            label="resources.payment.fields.createdTime"
            dateFormat={DATETIME_DISPLAY_FORMAT}
          />
          <DateField
            source="completedAt"
            sortable={false}
            label="resources.payment.fields.completedTime"
            dateFormat={DATETIME_DISPLAY_FORMAT}
          />
          <SelectField
            source="paymentMethod"
            label="resources.payment.fields.paymentMethod"
            choices={Object.values(PAYMENT_METHOD).map((method) => ({
              id: method,
              name: t(`common.paymentMethod.${method}`),
            }))}
            sortable={false}
          />
          <AmountField
            source="amount"
            sortable={false}
            label="resources.payment.fields.amount"
            symbol={(record) => record.currencyCode}
          />
          <CustomLinkField
            source="payer"
            sortable={false}
            label="resources.payment.fields.payer"
            component={(record) => (record.payerId ? Link : Box)}
            href={(record) => `/#/${resourceSlug.USER}/${record.payerId}/show`}
            title={(record) => record.payerUsername}
          />
          <FunctionField
            label="resources.payment.fields.description"
            sortable={false}
            render={(record) =>
              record.description ? (
                <Tooltip title={record.description}>
                  <Box>{truncate(record.description, 15)}</Box>
                </Tooltip>
              ) : (
                '-'
              )
            }
          />
          <FunctionField
            label="resources.payment.fields.proofs"
            render={(record) => <ProofsPreview urls={record.paymentProofUrls} />}
          />
          <FunctionField
            source="paymentStatus"
            sortable={false}
            label="resources.payment.fields.status"
            render={(record) => <PaymentStatus status={record.status} />}
          />
        </ArrayWithPaginationField>
      </Box>
    )
  );
});

export default PaymentTable;
